/**
 * Order journey — single source of truth for the role-aware lifecycle UI.
 *
 * Derives the current step (0-6) from the canonical backend fields:
 *   order.status                    (delivery state machine, unchanged)
 *   order.settlement.status         (settlement state machine, unchanged)
 *   order.fulfillment_milestone     (additive sub-state)
 *
 * 7-step journey, mirrored per role. Step indices are stable across roles so
 * the timeline component can render the same dots / line, just with role-specific
 * copy and CTAs.
 */

// step IDs are stable per role (0..6)
export const STEPS = {
  AWAIT_INVOICE: 0,
  INVOICE_VISIBLE: 1,
  PAYMENT: 2,
  IN_PROGRESS: 3,
  OUT_FOR_DELIVERY: 4,
  DELIVERED: 5,
  CLOSED: 6,
};

// --- Buyer journey ----------------------------------------------------------
export const BUYER_JOURNEY = [
  { id: STEPS.AWAIT_INVOICE, key: "awaiting_invoice", title: "Awaiting invoice", subtitle: "Your supplier is preparing the invoice." },
  { id: STEPS.INVOICE_VISIBLE, key: "invoice_received", title: "Invoice received", subtitle: "Review and pay your supplier off-platform." },
  { id: STEPS.PAYMENT, key: "payment_sent", title: "Payment sent", subtitle: "Supplier is confirming receipt." },
  { id: STEPS.IN_PROGRESS, key: "order_in_progress", title: "Order in progress", subtitle: "Being assembled and prepared for shipment." },
  { id: STEPS.OUT_FOR_DELIVERY, key: "out_for_delivery", title: "Out for delivery", subtitle: "On the way to you." },
  { id: STEPS.DELIVERED, key: "delivered", title: "Delivered", subtitle: "Confirm receipt to close the order." },
  { id: STEPS.CLOSED, key: "closed", title: "Closed", subtitle: "Order complete." },
];

// --- Supplier journey -------------------------------------------------------
export const SUPPLIER_JOURNEY = [
  { id: STEPS.AWAIT_INVOICE, key: "issue_invoice", title: "Issue invoice", subtitle: "Submit invoice number and date." },
  { id: STEPS.INVOICE_VISIBLE, key: "awaiting_payment", title: "Awaiting buyer payment", subtitle: "Buyer paying off-platform." },
  { id: STEPS.PAYMENT, key: "payment_received", title: "Payment received", subtitle: "Confirm receipt and begin fulfillment." },
  { id: STEPS.IN_PROGRESS, key: "order_assembly", title: "Order assembly", subtitle: "Assemble, pack, and prepare to ship." },
  { id: STEPS.OUT_FOR_DELIVERY, key: "out_for_delivery", title: "Out for delivery", subtitle: "Tracking added; carrier en route." },
  { id: STEPS.DELIVERED, key: "delivered", title: "Delivered", subtitle: "Buyer to confirm receipt." },
  { id: STEPS.CLOSED, key: "closed", title: "Closed", subtitle: "Order complete." },
];

/** Returns the role's journey array. */
export function journeyFor(role) {
  if (role === "supplier") return SUPPLIER_JOURNEY;
  return BUYER_JOURNEY; // buyer + admin both see the buyer-shaped narrative for clarity
}

/**
 * One-line stage label for an order — what the user sees on the Orders list
 * pill and the Order Detail header pill. Combines status + settlement +
 * fulfillment_milestone so that, e.g., a buyer with `status=pending_payment`
 * but `settlement.status=invoiced` correctly reads as "Invoice received" rather
 * than the stale "Awaiting invoice".
 */
export function journeyStageLabel(order, role) {
  if (!order) return "";
  if (isDisputed(order)) return "Disputed";
  const j = journeyFor(role);
  const step = currentStep(order);
  return j[step]?.title || "";
}

/** Derive current step index from order state. */
export function currentStep(order) {
  if (!order) return 0;
  const status = order.status;
  const settle = order.settlement?.status;
  const milestone = order.fulfillment_milestone;

  // Terminal states win
  if (status === "closed") return STEPS.CLOSED;
  if (status === "delivered") return STEPS.DELIVERED;
  if (status === "shipped") return STEPS.OUT_FOR_DELIVERY;
  if (status === "processing") {
    if (milestone === "picked_up" || milestone === "out_for_delivery") return STEPS.OUT_FOR_DELIVERY;
    return STEPS.IN_PROGRESS;
  }
  if (status === "payment_received") return STEPS.PAYMENT;
  // status pending_payment — look at settlement
  if (settle === "buyer_confirmed_paid") return STEPS.PAYMENT;
  if (settle === "invoiced") return STEPS.INVOICE_VISIBLE;
  if (settle === "awaiting_invoice" || !settle) return STEPS.AWAIT_INVOICE;
  return STEPS.AWAIT_INVOICE;
}

export function isDisputed(order) {
  return order?.settlement?.status === "disputed" || order?.status === "disputed";
}

/**
 * Next-action descriptor for the current step + role.
 * Returns { title, body, cta?, transition? } where transition is the
 * backend transition the CTA performs — UI invokes it via the existing helpers.
 *
 * CTAs intentionally match endpoints we already have. No new logic.
 */
export function nextAction(order, role) {
  if (!order || isDisputed(order)) {
    return order?.settlement?.dispute_ticket_id
      ? { title: "Dispute open", body: "A support ticket has been created. The team is reviewing.", ctaLabel: null }
      : null;
  }
  const step = currentStep(order);
  const milestone = order.fulfillment_milestone;

  if (role === "buyer") {
    switch (step) {
      case STEPS.AWAIT_INVOICE:
        return { title: "Awaiting invoice", body: "Your supplier is preparing the invoice. You'll be notified the moment it arrives.", passive: true };
      case STEPS.INVOICE_VISIBLE:
        return { title: "Pay the supplier off-platform", body: "Once you've sent payment via wire / EFT / cheque, mark it sent here to keep the supplier informed.", ctaLabel: "Mark payment sent", action: "mark_paid" };
      case STEPS.PAYMENT:
        return { title: "Payment sent", body: "Awaiting your supplier's confirmation of receipt.", passive: true };
      case STEPS.IN_PROGRESS:
        if (milestone === "ready_to_ship") return { title: "Ready to ship", body: "Your order is packed and ready for carrier pickup.", passive: true };
        return { title: "Order in progress", body: "Your order is being assembled and prepared for shipment.", passive: true };
      case STEPS.OUT_FOR_DELIVERY:
        return { title: "Out for delivery", body: order.tracking_number ? `Tracking: ${order.tracking_number}${order.tracking_carrier ? ` · ${order.tracking_carrier}` : ""}` : "On the way.", passive: true };
      case STEPS.DELIVERED:
        return { title: "Confirm receipt", body: "Confirm you've received your order to close it out. If something's off, open a dispute instead.", ctaLabel: "Confirm receipt", action: "close" };
      case STEPS.CLOSED:
        return { title: "Order complete", body: "This order is closed.", passive: true };
      default: return null;
    }
  }

  if (role === "supplier") {
    switch (step) {
      case STEPS.AWAIT_INVOICE:
        return { title: "Issue the invoice", body: "Submit your invoice number and date so the buyer can pay you off-platform.", ctaLabel: "Submit invoice", action: "issue_invoice" };
      case STEPS.INVOICE_VISIBLE:
        return { title: "Awaiting buyer payment", body: "Invoice is with the buyer. We'll alert you when they mark payment sent.", passive: true };
      case STEPS.PAYMENT:
        return { title: "Confirm payment received", body: "Buyer says payment is sent. Confirm receipt to start fulfillment.", ctaLabel: "Confirm payment received", action: "confirm_payment" };
      case STEPS.IN_PROGRESS:
        if (milestone === "ready_to_ship") return { title: "Hand off to carrier", body: "Order is ready. Once it's picked up, mark out for delivery and add tracking.", ctaLabel: "Add tracking & mark shipped", action: "ship" };
        if (milestone === "in_assembly") return { title: "Continue assembly", body: "When the order is packed and ready, mark it ready to ship.", ctaLabel: "Mark ready to ship", action: "milestone:ready_to_ship" };
        return { title: "Start assembly", body: "Begin order assembly to keep your buyer informed.", ctaLabel: "Start assembly", action: "milestone:in_assembly" };
      case STEPS.OUT_FOR_DELIVERY:
        return { title: "In transit", body: order.tracking_number ? `Tracking: ${order.tracking_number}${order.tracking_carrier ? ` · ${order.tracking_carrier}` : ""}` : "Awaiting delivery confirmation.", ctaLabel: "Mark delivered", action: "deliver" };
      case STEPS.DELIVERED:
        return { title: "Awaiting buyer confirmation", body: "Buyer will confirm receipt to close the order.", passive: true };
      case STEPS.CLOSED:
        return { title: "Order complete", body: "This order is closed.", passive: true };
      default: return null;
    }
  }

  // admin — read-only
  return { title: "Admin view", body: "Read-only. Platform never moves trade funds.", passive: true };
}
